'use client';

import { useEffect, useRef } from 'react';

interface Confetti {
  x: number;
  y: number;
  size: number;
  speed: number;
  wind: number;
  opacity: number;
  color: string;
  rotation: number;
  rotationSpeed: number;
  shape: 'circle' | 'square' | 'triangle';
}

interface Firework {
  x: number;
  y: number;
  targetY: number;
  speed: number;
  color: string;
  exploded: boolean;
  particles: Particle[];
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  size: number;
  opacity: number;
  life: number;
  maxLife: number;
}

interface NewYearAnimationProps {
  containerRef?: React.RefObject<HTMLElement>;
}

export default function NewYearAnimation({
  containerRef,
}: NewYearAnimationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameIdRef = useRef<number | undefined>(undefined);
  const lastFrameTimeRef = useRef<number>(0);
  const isVisibleRef = useRef<boolean>(true);
  const lastFireworkTimeRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef?.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    // Set canvas size based on container
    const resizeCanvas = () => {
      if (container) {
        const rect = container.getBoundingClientRect();
        canvas.width = rect.width;
        canvas.height = rect.height;
      } else {
        canvas.width = window.innerWidth;
        canvas.height = Math.min(window.innerHeight * 0.5, 400);
      }
    };
    resizeCanvas();

    // Intersection Observer to pause when not visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isVisibleRef.current = entry.isIntersecting;
        });
      },
      { threshold: 0.1 }
    );

    if (container) {
      observer.observe(container);
    } else {
      observer.observe(canvas);
    }

    window.addEventListener('resize', resizeCanvas);

    // Colors for confetti and fireworks
    const colors = [
      '#ff2929', // Red
      '#4ECDC4', // Turquoise
      '#FFE66D', // Yellow
      '#A8E6CF', // Mint
      '#FF8B94', // Pink
      '#C7CEEA', // Lavender
      '#FFDAC1', // Peach
      '#B4F8C8', // Light Green
      '#FBE7C6', // Cream
      '#A0E7E5', // Cyan
    ];

    // Create confetti
    const confettiArray: Confetti[] = [];
    const numberOfConfetti = 80;

    for (let i = 0; i < numberOfConfetti; i++) {
      confettiArray.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height - canvas.height,
        size: Math.random() * 8 + 4,
        speed: Math.random() * 1.2 + 0.6,
        wind: Math.random() * 0.6 - 0.3,
        opacity: Math.random() * 0.5 + 0.5,
        color: colors[Math.floor(Math.random() * colors.length)],
        rotation: Math.random() * 360,
        rotationSpeed: Math.random() * 4 - 2,
        shape: ['circle', 'square', 'triangle'][
          Math.floor(Math.random() * 3)
        ] as 'circle' | 'square' | 'triangle',
      });
    }

    // Create fireworks array
    const fireworks: Firework[] = [];

    // Function to create a new firework
    const createFirework = () => {
      const color = colors[Math.floor(Math.random() * colors.length)];
      fireworks.push({
        x: Math.random() * canvas.width,
        y: canvas.height,
        targetY: Math.random() * (canvas.height * 0.3) + canvas.height * 0.1,
        speed: Math.random() * 3 + 4,
        color,
        exploded: false,
        particles: [],
      });
    };

    // Draw confetti shapes
    const drawConfetti = (confetti: Confetti) => {
      ctx.save();
      ctx.translate(confetti.x, confetti.y);
      ctx.rotate((confetti.rotation * Math.PI) / 180);
      ctx.globalAlpha = confetti.opacity;
      ctx.fillStyle = confetti.color;

      switch (confetti.shape) {
        case 'circle':
          ctx.beginPath();
          ctx.arc(0, 0, confetti.size / 2, 0, Math.PI * 2);
          ctx.fill();
          break;
        case 'square':
          ctx.fillRect(
            -confetti.size / 2,
            -confetti.size / 2,
            confetti.size,
            confetti.size
          );
          break;
        case 'triangle':
          ctx.beginPath();
          ctx.moveTo(0, -confetti.size / 2);
          ctx.lineTo(confetti.size / 2, confetti.size / 2);
          ctx.lineTo(-confetti.size / 2, confetti.size / 2);
          ctx.closePath();
          ctx.fill();
          break;
      }

      ctx.restore();
    };

    // Explode firework into particles
    const explodeFirework = (firework: Firework) => {
      const particleCount = 30;
      for (let i = 0; i < particleCount; i++) {
        const angle = (Math.PI * 2 * i) / particleCount;
        const speed = Math.random() * 3 + 2;
        const life = Math.random() * 30 + 30;

        firework.particles.push({
          x: firework.x,
          y: firework.y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          color: firework.color,
          size: Math.random() * 3 + 2,
          opacity: 1,
          life,
          maxLife: life,
        });
      }
      firework.exploded = true;
    };

    // Animation loop - throttled to 30fps
    const targetFPS = 30;
    const frameInterval = 1000 / targetFPS;

    const animate = (currentTime: number) => {
      // Only animate if visible
      if (!isVisibleRef.current) {
        animationFrameIdRef.current = requestAnimationFrame(animate);
        return;
      }

      // Throttle to 30fps
      const elapsed = currentTime - lastFrameTimeRef.current;
      if (elapsed < frameInterval) {
        animationFrameIdRef.current = requestAnimationFrame(animate);
        return;
      }

      lastFrameTimeRef.current = currentTime - (elapsed % frameInterval);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Create new fireworks periodically
      if (currentTime - lastFireworkTimeRef.current > 800) {
        createFirework();
        lastFireworkTimeRef.current = currentTime;
      }

      // Draw and update confetti
      confettiArray.forEach((confetti) => {
        drawConfetti(confetti);

        // Update position
        confetti.y += confetti.speed;
        confetti.x += confetti.wind;
        confetti.rotation += confetti.rotationSpeed;

        // Reset confetti when it goes off screen
        if (confetti.y > canvas.height + 10) {
          confetti.y = -10;
          confetti.x = Math.random() * canvas.width;
        }

        // Keep within horizontal bounds
        if (confetti.x > canvas.width) {
          confetti.x = 0;
        } else if (confetti.x < 0) {
          confetti.x = canvas.width;
        }
      });

      // Draw and update fireworks
      fireworks.forEach((firework, index) => {
        if (!firework.exploded) {
          // Draw rising firework
          ctx.beginPath();
          ctx.arc(firework.x, firework.y, 3, 0, Math.PI * 2);
          ctx.fillStyle = firework.color;
          ctx.fill();

          // Update position
          firework.y -= firework.speed;

          // Explode when reaching target height
          if (firework.y <= firework.targetY) {
            explodeFirework(firework);
          }
        } else {
          // Draw and update particles
          firework.particles.forEach((particle, pIndex) => {
            ctx.save();
            ctx.globalAlpha = particle.opacity;
            ctx.beginPath();
            ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
            ctx.fillStyle = particle.color;
            ctx.fill();
            ctx.restore();

            // Update particle
            particle.x += particle.vx;
            particle.y += particle.vy;
            particle.vy += 0.1; // Gravity
            particle.life--;
            particle.opacity = particle.life / particle.maxLife;

            // Remove dead particles
            if (particle.life <= 0) {
              firework.particles.splice(pIndex, 1);
            }
          });

          // Remove firework when all particles are gone
          if (firework.particles.length === 0) {
            fireworks.splice(index, 1);
          }
        }
      });

      animationFrameIdRef.current = requestAnimationFrame(animate);
    };

    animationFrameIdRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      observer.disconnect();
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
    };
  }, [containerRef]);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0 z-10"
      style={{ willChange: 'transform' }}
      aria-hidden="true"
    />
  );
}
